// Whether the athlete's v2 coach is known yet, read from /v2/me (or
// /v2/me/viewing for a coach viewing one). Every athlete is v2; whatever
// coach_mode says (or doesn't), an answer is a v2 answer. Three states:
//   - 'v2': /v2/me answered (a cached answer also counts until the fresh read
//     lands, so a cold load starts the v2 client at once);
//   - 'none': a coach viewing someone whose /v2/me/viewing refused (403: not
//     their coach), so there is no athlete coach to show;
//   - 'unknown': not answered yet, or the read failed. The app shows a
//     loading or "Reconnecting to your coach…" state.
// A failed read is retried with backoff (1 s, 2 s, 5 s, 10 s, then every
// 30 s) and at once when the window regains focus or comes back online.
//
// The last good /v2/me is kept per user id in localStorage (the store is
// optional: private windows and blocked site data just lose the head start).

/** What the app says while /v2/me is failing (and retrying). */
export const RECONNECTING = 'Reconnecting to your coach…';

export const ME_RETRY_MS = [1000, 2000, 5000, 10000, 30000];
const cacheKey = (userId) => `aithlo-v2-me:${userId}`;

const storageOf = (storage) => {
  if (storage !== undefined) return storage;
  try { return globalThis.localStorage || null; } catch { return null; }
};

/** The last /v2/me this browser read for `userId`, or null. */
export function loadMe(userId, storage) {
  const s = storageOf(storage);
  if (!userId || !s) return null;
  try {
    const raw = s.getItem(cacheKey(userId));
    const me = raw ? JSON.parse(raw) : null;
    return me && typeof me === 'object' ? me : null;
  } catch {
    return null;
  }
}

export function saveMe(userId, me, storage) {
  const s = storageOf(storage);
  if (!userId || !s) return;
  try { s.setItem(cacheKey(userId), JSON.stringify(me)); } catch { /* the head start is optional */ }
}

/**
 * The coach state from what is known: `answer` is the fresh read's result
 * ({ data } once answered, null before), `cached` the stored /v2/me.
 */
export function coachModeOf({ answer = null, cached = null } = {}) {
  if (answer) return answer.data ? 'v2' : 'none';
  if (cached) return 'v2';
  return 'unknown';
}

/**
 * Reads `read()` until it answers. `onChange({ answer, failed, attempts })`
 * after every attempt: `answer` is { data } once read (data may be null for
 * an answer that means "not a v2 athlete here", which `read` signals by
 * resolving null), `failed` true while the latest attempt failed.
 * `stop()` cancels the timers and listeners.
 */
export function createMeReader({
  read, onChange, win = globalThis.window, delays = ME_RETRY_MS,
  setTimer = (fn, ms) => setTimeout(fn, ms), clearTimer = (t) => clearTimeout(t),
}) {
  let stopped = false;
  let answered = false;
  let inFlight = false;
  let attempts = 0;
  let timer = null;

  const schedule = () => {
    if (stopped || answered) return;
    const ms = delays[Math.min(attempts - 1, delays.length - 1)];
    timer = setTimer(attempt, ms);
  };

  async function attempt() {
    if (stopped || answered || inFlight) return;
    if (timer != null) { clearTimer(timer); timer = null; }
    inFlight = true;
    attempts += 1;
    try {
      const data = await read();
      if (stopped) return;
      answered = true;
      onChange?.({ answer: { data: data ?? null }, failed: false, attempts });
      detach();
    } catch {
      if (stopped) return;
      onChange?.({ answer: null, failed: true, attempts });
      schedule();
    } finally {
      inFlight = false;
    }
  }

  const nudge = () => { if (!answered) attempt(); };
  const detach = () => {
    win?.removeEventListener?.('focus', nudge);
    win?.removeEventListener?.('online', nudge);
  };
  win?.addEventListener?.('focus', nudge);
  win?.addEventListener?.('online', nudge);

  return {
    start: () => { attempt(); },
    retry: nudge,
    stop: () => {
      stopped = true;
      if (timer != null) clearTimer(timer);
      detach();
    },
  };
}

/**
 * The coach-view count (/coaching/unread-count, polled) runs only while a
 * coach views an athlete: the counts are then the coach's own,
 * not the athlete's the v2 state carries. Everyone's own human-coach badge is
 * threadView.coachingUnread (aithlo-frontend#67).
 */
export const opensCoachHub = ({ viewing = false } = {}) => Boolean(viewing);

/**
 * Which coach panel the app mounts: 'v2' (CoachThread, once the client is
 * running), 'none' (nothing to show: a coach viewing someone not theirs), or
 * 'connecting' (not known yet, or the v2 client is still starting).
 */
export function coachPanel({ coachMode, v2Enabled }) {
  if (v2Enabled) return 'v2';
  if (coachMode === 'none') return 'none';
  return 'connecting';
}
